/**
 * Driver-self-service — Documents Panel (Plan §2.2 M-F-21).
 * Phase 4 G5. Consumes GET /driver/me/documents (DRIVER_OWN_DOCUMENT_VIEW).
 *
 * Mounted inside DriverDashboard. Driver-only own-scope.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { FileText, Upload } from 'lucide-react';
import DriverDocumentUploadModal from './DriverDocumentUploadModal';

const API_URL = (import.meta.env.VITE_BACKEND_URL || '') + '/api';

const DOC_TYPE_LABELS = {
  permis_de_conduire:       'Permis de conduire',
  carte_grise:              'Carte grise',
  attestation_assurance:    "Attestation d'assurance",
  capacite_professionnelle: 'Capacité professionnelle',
  kbis:                     'Extrait Kbis',
  casier_judiciaire:        'Casier judiciaire',
  justificatif_domicile:    'Justificatif de domicile',
  identity_document:        "Pièce d'identité",
};

const STATUS_COLOR = {
  pending:  'text-amber-400',
  verified: 'text-emerald-400',
  rejected: 'text-rose-400',
  expired:  'text-orange-400',
};

const STATUS_LABEL = {
  pending:  'En attente',
  verified: 'Vérifié',
  rejected: 'Rejeté',
  expired:  'Expiré',
};

const DriverDocumentsSelfServicePanel = ({ token, theme }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/driver/me/documents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDocs(Array.isArray(res.data) ? res.data : []);
      setError(null);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className={`${theme.bgCard} ${theme.border} border rounded-2xl p-4`} data-testid="driver-self-documents">
      <div className="flex items-center justify-between mb-3">
        <h3 className={`${theme.text} text-base font-semibold flex items-center gap-2`}>
          <FileText className="h-5 w-5" />
          Mes documents
        </h3>
        <button
          type="button"
          onClick={() => setUploadOpen(true)}
          className={`text-amber-400 hover:text-amber-300 text-sm inline-flex items-center gap-1`}
          data-testid="driver-self-upload-btn"
        >
          <Upload className="h-4 w-4" />
          Téléverser
        </button>
      </div>
      {loading && <p className={`${theme.textSub} text-sm`}>Chargement…</p>}
      {error && <p className="text-rose-400 text-sm">Erreur</p>}
      {!loading && !error && docs.length === 0 && (
        <p className={`${theme.textSub} text-sm`}>Aucun document téléversé pour l'instant.</p>
      )}
      {!loading && !error && docs.length > 0 && (
        <ul className={`${theme.divider} divide-y text-sm`}>
          {docs.map((d) => (
            <li key={d.id} className="py-2 flex items-center justify-between">
              <div>
                <div className={theme.text}>{DOC_TYPE_LABELS[d.document_type] || d.document_type}</div>
                <div className={`${theme.textMuted} text-xs`}>v{d.version} · {d.expiry_date ? `Exp. ${d.expiry_date.slice(0, 10)}` : 'Sans expiration'}</div>
              </div>
              <span className={`${STATUS_COLOR[d.verification_status] || theme.textSub} text-xs font-medium`}>
                {STATUS_LABEL[d.verification_status] || d.verification_status}
              </span>
            </li>
          ))}
        </ul>
      )}

      {uploadOpen && (
        <DriverDocumentUploadModal
          token={token}
          theme={theme}
          onClose={() => setUploadOpen(false)}
          onSuccess={() => { setUploadOpen(false); load(); }}
        />
      )}
    </div>
  );
};

export default DriverDocumentsSelfServicePanel;
